import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { firstValueFrom, take } from 'rxjs';

import { AuthService } from './authService.service';

type PortalSessionResult =
  | {
      readonly status: 'authenticated';
      readonly source: 'existing' | 'redirect' | 'cached' | 'silent';
    }
  | { readonly status: 'interaction-required' }
  | { readonly status: 'failed' };

type AzureIdTokenResult =
  | {
      readonly status: 'acquired';
      readonly idToken: string;
      readonly source: 'redirect' | 'cached' | 'silent';
    }
  | { readonly status: 'interaction-required' }
  | { readonly status: 'failed' };

@Injectable({
  providedIn: 'root'
})
export class MsalBootstrapService {
  #ready?: Promise<void>;
  #redirectResult: AuthenticationResult | null = null;

  ensureReady(msalService: MsalService, authService: AuthService): Promise<void> {
    if (!this.#ready) {
      const initialization = this.#bootstrap(msalService, authService);
      this.#ready = initialization;
      void initialization.catch(() => {
        if (this.#ready === initialization) {
          this.#ready = undefined;
        }
      });
    }

    return this.#ready;
  }

  getRedirectResult(): AuthenticationResult | null {
    return this.#redirectResult;
  }

  async completePortalSessionIfNeeded(
    msalService: MsalService,
    authService: AuthService
  ): Promise<PortalSessionResult> {
    try {
      await this.ensureReady(msalService, authService);
    } catch {
      return { status: 'failed' };
    }

    const account = msalService.instance.getActiveAccount();
    if (!account) {
      return { status: 'interaction-required' };
    }

    if (authService.restoreSessionIfValid()) {
      return { status: 'authenticated', source: 'existing' };
    }

    const tokenResult = await this.#recoverAzureIdToken(msalService, authService);
    if (tokenResult.status !== 'acquired') {
      return tokenResult;
    }

    authService.setAzureIdToken(tokenResult.idToken);

    try {
      const loginSucceeded = await firstValueFrom(
        authService.login(account.username, tokenResult.idToken).pipe(take(1))
      );

      return loginSucceeded && authService.restoreSessionIfValid()
        ? { status: 'authenticated', source: tokenResult.source }
        : { status: 'failed' };
    } catch {
      return { status: 'failed' };
    }
  }

  reset(): void {
    this.#ready = undefined;
    this.#redirectResult = null;
  }

  async #recoverAzureIdToken(
    msalService: MsalService,
    authService: AuthService
  ): Promise<AzureIdTokenResult> {
    const redirectIdToken = this.#redirectResult?.idToken;
    this.#redirectResult = null;
    if (redirectIdToken) {
      return { status: 'acquired', idToken: redirectIdToken, source: 'redirect' };
    }

    const cachedIdToken = authService.getAzureIdToken();
    if (cachedIdToken) {
      return { status: 'acquired', idToken: cachedIdToken, source: 'cached' };
    }

    try {
      const account = msalService.instance.getActiveAccount();
      if (!account) {
        return { status: 'interaction-required' };
      }

      const result = await msalService.instance.acquireTokenSilent({
        account,
        scopes: ['openid', 'profile', 'offline_access']
      });
      if (!result.idToken) {
        return { status: 'failed' };
      }

      authService.setAzureIdToken(result.idToken);
      return { status: 'acquired', idToken: result.idToken, source: 'silent' };
    } catch (error) {
      return isInteractionRequired(error)
        ? { status: 'interaction-required' }
        : { status: 'failed' };
    }
  }

  async #bootstrap(msalService: MsalService, authService: AuthService): Promise<void> {
    await msalService.instance.initialize();

    const redirectResult = await msalService.instance.handleRedirectPromise();
    this.#redirectResult = redirectResult;

    if (redirectResult?.account) {
      msalService.instance.setActiveAccount(redirectResult.account);

      if (redirectResult.idToken) {
        authService.setAzureIdToken(redirectResult.idToken);
      }

      return;
    }

    if (!msalService.instance.getActiveAccount()) {
      const accounts = msalService.instance.getAllAccounts();

      if (accounts.length > 0) {
        msalService.instance.setActiveAccount(accounts[0]);
      }
    }
  }
}

function isInteractionRequired(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }

  if (typeof error !== 'object' || error === null || !('errorCode' in error)) {
    return false;
  }

  const errorCode = String((error as { errorCode: unknown }).errorCode).toLowerCase();
  return errorCode.includes('interaction_required') ||
    errorCode.includes('login_required') ||
    errorCode.includes('consent_required');
}
